/**
 * Coinbase Wallet Provider
 * Multi-chain wallet supporting EVM and Solana chains
 */

import type {
  ChainType,
  WalletCategory,
  UnifiedAccount,
} from "@/types/wallet";
import { SolanaAdapter, isSolanaProvider } from "../adapters/SolanaAdapter";
import { DEFAULT_SOLANA_CLUSTER, EVM_CHAIN_IDS } from "@/config/chains";
import { BaseMultiChainProvider, type SecondaryChainAdapter } from "./BaseMultiChainProvider";

/**
 * CoinbaseProvider - Multi-chain wallet for EVM and Solana
 * Coinbase Wallet is the recommended wallet for Base chain with expanding multi-chain support
 */
export class CoinbaseProvider extends BaseMultiChainProvider {
  readonly name = "Coinbase Wallet";
  readonly icon = "coinbase";
  readonly category: WalletCategory = "multichain";
  readonly supportedChainTypes: ChainType[] = ["evm", "solana"];

  private solanaAdapter: SolanaAdapter | null = null;

  protected readonly secondaryChainType: ChainType = "solana";

  /**
   * Get underlying provider objects
   */
  get providers() {
    return {
      evm: this.getEVMProvider(),
      solana: CoinbaseProvider.getSolanaProvider(),
    };
  }

  /** @returns The Solana adapter instance, or null if not connected */
  protected getSecondaryAdapter(): SecondaryChainAdapter | null {
    return this.solanaAdapter;
  }

  /** Clears the Solana adapter reference during disconnect */
  protected clearSecondaryAdapter(): void {
    this.solanaAdapter = null;
  }

  /**
   * Check if Coinbase Wallet is installed
   * @returns True if Coinbase Wallet extension is available
   */
  isInstalled(): boolean {
    if (this.supportedChainTypes.length === 0) return false;
    if (typeof window === "undefined") return false;

    // Check for Coinbase Wallet extension
    const hasCoinbaseExtension =
      typeof window.coinbaseWalletExtension !== "undefined";

    // Check for Coinbase injected into ethereum
    const hasCoinbaseInEthereum = window.ethereum?.isCoinbaseWallet ?? false;

    return hasCoinbaseExtension || hasCoinbaseInEthereum;
  }

  /**
   * Check if Coinbase Solana is available
   * @returns True if Coinbase Solana provider exists
   */
  hasSolanaSupport(): boolean {
    if (!this.supportedChainTypes.includes("solana")) return false;
    const provider = CoinbaseProvider.getSolanaProvider();
    return isSolanaProvider(provider);
  }

  /**
   * Default to Base chain for Coinbase Wallet users
   */
  protected override defaultEVMChainId = EVM_CHAIN_IDS.BASE;

  /**
   * Get Coinbase EVM provider from window
   */
  protected getEVMProvider(): unknown {
    if (!this.isInstalled()) return null;

    // Prefer dedicated extension
    if (window.coinbaseWalletExtension) {
      return window.coinbaseWalletExtension;
    }

    // Fall back to injected ethereum
    if (window.ethereum?.isCoinbaseWallet) {
      return window.ethereum;
    }

    return null;
  }

  /**
   * Connect to Coinbase Solana provider
   * @returns Array of Solana accounts
   */
  protected async connectSecondary(): Promise<UnifiedAccount[]> {
    if (!this.hasSolanaSupport()) {
      throw new Error("Coinbase Wallet Solana support not available");
    }

    const solanaProvider = CoinbaseProvider.getSolanaProvider();
    if (!solanaProvider || !isSolanaProvider(solanaProvider)) {
      throw new Error("Coinbase Wallet Solana provider not available");
    }

    this.solanaAdapter = new SolanaAdapter(solanaProvider, DEFAULT_SOLANA_CLUSTER);
    return await this.solanaAdapter.connect();
  }

  /**
   * Get Coinbase Solana provider from window
   * Note: Coinbase Wallet may inject Solana provider differently
   */
  private static getSolanaProvider(): unknown {
    if (typeof window === "undefined") return null;

    // Check for Coinbase-specific Solana provider
    const solana = (window as { solana?: { isCoinbaseWallet?: boolean } }).solana;
    if (solana?.isCoinbaseWallet) {
      return solana;
    }

    return null;
  }
}

/**
 * Create a Coinbase Wallet provider instance
 * @returns CoinbaseProvider if available, null otherwise
 */
export function createCoinbaseProvider(): CoinbaseProvider | null {
  const provider = new CoinbaseProvider();
  return provider.isInstalled() ? provider : null;
}
